import { useState, useEffect } from "react";
import {
  useCreate,
  useUpdate,
  useNotify,
  useRefresh,
  useTranslate,
  useGetList,
  useGetOne,
  useGetIdentity,
  type Identifier,
} from "ra-core";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  Payment,
  Contract,
  Account,
  TransactionCategory,
} from "../../types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: Payment;
}

export const MarkPaymentPaidSheet = ({ open, onOpenChange, payment }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const direction = payment.direction ?? "income";
  const isExpense = direction === "expense";

  const [accountId, setAccountId] = useState<string>(
    payment.account_id ? String(payment.account_id) : "",
  );
  const [categoryId, setCategoryId] = useState<string>(
    payment.category_id ? String(payment.category_id) : "",
  );
  const [occurredAt, setOccurredAt] = useState<string>(
    payment.paid_date ?? new Date().toISOString().slice(0, 10),
  );
  const [description, setDescription] = useState<string>("");

  const { data: accounts } = useGetList<Account>("accounts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
    filter: { "archived_at@is": null },
  });
  const { data: categories } = useGetList<TransactionCategory>(
    "transaction_categories",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
      filter: { direction, "archived_at@is": null },
    },
  );
  const { data: contract } = useGetOne<Contract>(
    "contracts",
    { id: payment.contract_id as Identifier },
    { enabled: !!payment.contract_id },
  );

  useEffect(() => {
    if (!accountId && accounts?.length) {
      if (payment.account_id) {
        setAccountId(String(payment.account_id));
      } else {
        setAccountId(String(accounts[0].id));
      }
    }
  }, [accounts, accountId, payment.account_id]);

  useEffect(() => {
    if (!categoryId && categories?.length) {
      const dfltName = isExpense ? "Прочие расходы" : "Поступления по договорам";
      const dflt =
        categories.find((c) => c.name === dfltName) ?? categories[0];
      setCategoryId(String(dflt.id));
    }
  }, [categories, categoryId, isExpense]);

  useEffect(() => {
    if (!description) {
      if (contract) {
        setDescription(
          isExpense
            ? `Выплата #${payment.id} по договору «${contract.title}»`
            : `Платёж #${payment.id} по договору «${contract.title}»`,
        );
      } else if (payment.vendor_name) {
        setDescription(
          isExpense
            ? `Выплата #${payment.id}: ${payment.vendor_name}`
            : `Поступление #${payment.id}: ${payment.vendor_name}`,
        );
      } else if (payment.comment) {
        setDescription(payment.comment);
      }
    }
  }, [contract, payment.id, payment.vendor_name, payment.comment, description, isExpense]);

  const [updatePayment, { isPending: isUpdating }] = useUpdate();
  const [createTx, { isPending: isCreating }] = useCreate();

  const submit = async () => {
    if (!accountId) {
      notify(
        translate("finance.markPaid.accountRequired", { _: "Выберите счёт" }),
        { type: "warning" },
      );
      return;
    }
    try {
      await updatePayment(
        "payments",
        {
          id: payment.id,
          data: { status: "paid", paid_date: occurredAt },
          previousData: payment,
        },
        { returnPromise: true },
      );
      await createTx(
        "transactions",
        {
          data: {
            account_id: Number(accountId),
            category_id: categoryId ? Number(categoryId) : null,
            direction,
            amount: payment.amount,
            occurred_at: occurredAt,
            description: description || null,
            payment_id: payment.id,
            contract_id: payment.contract_id ?? null,
            deal_id: contract?.deal_id ?? null,
            company_id: contract?.company_id ?? null,
            sales_id: identity?.id ?? null,
          },
        },
        { returnPromise: true },
      );
      notify(
        translate("finance.markPaid.success", { _: "Операция проведена" }),
        { type: "info" },
      );
      onOpenChange(false);
      refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notify(msg, { type: "error" });
    }
  };

  const title = isExpense
    ? translate("finance.markPaid.titleExpense", { _: "Провести выплату" })
    : translate("finance.markPaid.title", { _: "Принять оплату" });
  const accountLabel = isExpense
    ? translate("finance.markPaid.accountExpense", { _: "Счёт списания" })
    : translate("finance.markPaid.account", { _: "Счёт зачисления" });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4 px-4">
          <div className="space-y-1">
            <Label>{accountLabel}</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {accounts?.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>
              {translate("finance.markPaid.category", { _: "Категория" })}
            </Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {categories?.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{translate("finance.markPaid.date", { _: "Дата" })}</Label>
            <Input
              type="date"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>
              {translate("finance.markPaid.description", { _: "Описание" })}
            </Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <SheetFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpdating || isCreating}
          >
            {translate("ra.action.cancel", { _: "Отмена" })}
          </Button>
          <Button onClick={submit} disabled={isUpdating || isCreating}>
            {isExpense
              ? translate("finance.markPaid.submitExpense", { _: "Провести" })
              : translate("finance.markPaid.submit", { _: "Принять" })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
